"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button, Card, Input } from "@heroui/react";
import {
  LockFilled,
  CheckCircleFilled,
  AlertCircle,
  Loader2,
  EyeFilled,
  CodeFilled,
  MarkdownFilled,
  ShieldFilled,
  IconGithub,
  KeyFilled,
} from "@/components/icons";
import { CodeBlock } from "@/components/shared/code-block";
import { demoPasswordBreaches, type PasswordBreachResult } from "@/lib/api";
import { BREACHES_DEMO_EXAMPLES } from "./breaches-data";

type IconType = React.ComponentType<{ className?: string }>;

type Check = "password" | "github" | "exposed";
type Severity = "critical" | "high" | "medium" | "low" | "unknown";

interface LeakFinding {
  rule_id: string;
  severity: Severity;
  file: string;
  start_line: number;
  preview: string;
  validation?: "valid" | "invalid" | "undetermined";
}

interface ScanResult {
  check: "github" | "exposed";
  scan_id: string;
  target: string;
  total: number;
  by_severity: Partial<Record<Severity, number>>;
  findings: LeakFinding[];
  live?: number;
  credits: number;
}

// Password uses the live k-anonymity endpoint; the two scan checks replay canned
// findings client-side because they have no public demo endpoint yet.
type DemoResult = ({ check: "password" } & PasswordBreachResult) | ScanResult;

function isPassword(r: DemoResult): r is { check: "password" } & PasswordBreachResult {
  return r.check === "password";
}

/* ── Canned scan results (safe, illustrative only) ─────── */

const GITHUB_LEAKS: ScanResult = {
  check: "github",
  scan_id: "scn_gh01",
  target: "github.com/acme/legacy-api",
  total: 3,
  by_severity: { critical: 1, high: 1, medium: 1 },
  findings: [
    { rule_id: "aws-access-key", severity: "critical", file: "config/deploy.rb", start_line: 42, preview: "AKIA****************" },
    { rule_id: "openai-api-key", severity: "high", file: ".env.example", start_line: 8, preview: "sk-proj-****...****" },
    { rule_id: "generic-api-key", severity: "medium", file: "src/client.js", start_line: 15, preview: "key_****9f2a" },
  ],
  credits: 3,
};

const GITHUB_CLEAN: ScanResult = {
  check: "github",
  scan_id: "scn_gh02",
  target: "github.com/acme/marketing-site",
  total: 0,
  by_severity: {},
  findings: [],
  credits: 0,
};

const EXPOSED_LIVE: ScanResult = {
  check: "exposed",
  scan_id: "scn_ek01",
  target: "paste.ee/r/aB12x",
  total: 2,
  by_severity: { critical: 1, high: 1 },
  findings: [
    { rule_id: "stripe-secret-key", severity: "critical", file: "paste", start_line: 3, preview: "sk_live_****", validation: "valid" },
    { rule_id: "github-pat", severity: "high", file: "paste", start_line: 7, preview: "ghp_****", validation: "invalid" },
  ],
  live: 1,
  credits: 2,
};

const EXPOSED_CLEAN: ScanResult = {
  check: "exposed",
  scan_id: "scn_ek02",
  target: "gist.github.com/acme/snippet",
  total: 0,
  by_severity: {},
  findings: [],
  live: 0,
  credits: 0,
};

// Shown in the results card before the first real run, so a tab never starts empty.
const PASSWORD_EXAMPLE: DemoResult = {
  check: "password",
  prefix: "5BAA6",
  found: true,
  count: 1203,
  message: "This password has appeared in known breaches - do not use it.",
  credits: 1,
};

/* ── Per-check config ───────────────────────────────── */

interface SampleChip {
  label: string;
  /** Password samples carry a string; scan samples carry a canned result. */
  value?: string;
  scan?: ScanResult;
}

interface CheckTab {
  id: Check;
  label: string;
  description: string;
  icon: IconType;
  endpoint: string;
  placeholder: string;
  cta: string;
  tag?: string;
  example: DemoResult;
  samples: SampleChip[];
}

const CHECKS: CheckTab[] = [
  {
    id: "password",
    label: "Password",
    description: "Check whether a password appears in known data breaches.",
    icon: LockFilled,
    endpoint: "https://developer.encrata.com/api/lookup/email/password/breaches",
    placeholder: "Enter a password to check...",
    cta: "Try it!",
    example: PASSWORD_EXAMPLE,
    samples: BREACHES_DEMO_EXAMPLES.map((p) => ({ label: p, value: p })),
  },
  {
    id: "github",
    label: "GitHub leaks",
    description: "Scan a repository and its git history for exposed secrets.",
    icon: IconGithub,
    endpoint: "https://developer.encrata.com/api/breaches/github",
    placeholder: "Paste a public repo URL...",
    cta: "Scan it!",
    example: GITHUB_LEAKS,
    samples: [
      { label: "Repo with leaks", scan: GITHUB_LEAKS },
      { label: "Clean repo", scan: GITHUB_CLEAN },
    ],
  },
  {
    id: "exposed",
    label: "Exposed keys",
    description: "Find API keys exposed in public code, gists and paste sites.",
    icon: KeyFilled,
    endpoint: "https://developer.encrata.com/api/breaches/exposed-keys",
    placeholder: "Paste code or a snippet to scan...",
    cta: "Scan it!",
    example: EXPOSED_LIVE,
    samples: [
      { label: "Leaked paste", scan: EXPOSED_LIVE },
      { label: "Clean gist", scan: EXPOSED_CLEAN },
    ],
  },
];

const SEVERITY_TINT: Record<Severity, string> = {
  critical: "bg-danger/10 text-danger",
  high: "bg-danger/10 text-danger",
  medium: "bg-warning/10 text-warning",
  low: "bg-warning/10 text-warning",
  unknown: "bg-sidebar text-text-tertiary dark:bg-card",
};

type OutputView = "visual" | "json" | "markdown";

const OUTPUT_TABS: { id: OutputView; label: string; icon: IconType }[] = [
  { id: "visual", label: "Visual", icon: EyeFilled },
  { id: "json", label: "JSON", icon: CodeFilled },
  { id: "markdown", label: "Markdown", icon: MarkdownFilled },
];

function verdictOf(r: DemoResult): { danger: boolean; title: string; subtitle: string; Icon: IconType } {
  if (isPassword(r)) {
    return r.found
      ? { danger: true, title: "Found in breaches", subtitle: `Seen ${r.count.toLocaleString()} times - do not use this password.`, Icon: LockFilled }
      : { danger: false, title: "Not found in any breach", subtitle: "This password hasn't appeared in known breach corpora.", Icon: CheckCircleFilled };
  }
  if (r.total > 0) {
    const live = r.check === "exposed" && r.live ? `, ${r.live} still live` : "";
    return {
      danger: true,
      title: `${r.total} secret${r.total === 1 ? "" : "s"} found`,
      subtitle: `Hardcoded credentials detected in ${r.target}${live}.`,
      Icon: ShieldFilled,
    };
  }
  return { danger: false, title: "No secrets found", subtitle: `Nothing exposed in ${r.target}.`, Icon: CheckCircleFilled };
}

function metasFor(r: DemoResult): { k: string; v: string; mono?: boolean }[] {
  if (isPassword(r)) {
    return [
      { k: "Hash prefix", v: r.prefix, mono: true },
      { k: "Times seen", v: r.count.toLocaleString() },
      { k: "Credits", v: String(r.credits) },
    ];
  }
  const metas = [{ k: "Secrets", v: String(r.total) }];
  if (r.check === "exposed") metas.push({ k: "Live keys", v: String(r.live ?? 0) });
  else metas.push({ k: "Files", v: String(new Set(r.findings.map((f) => f.file)).size) });
  metas.push({ k: "Credits", v: String(r.credits) });
  return metas;
}

function resultMarkdown(r: DemoResult): string {
  if (isPassword(r)) {
    return [
      "# Password breach check",
      "",
      `- **Hash prefix:** \`${r.prefix}\``,
      `- **Found:** ${r.found ? "Yes" : "No"}`,
      `- **Times seen:** ${r.count.toLocaleString()}`,
      `- **Credits:** ${r.credits}`,
      "",
      `> ${r.message}`,
    ].join("\n");
  }
  const heading = r.check === "github" ? "GitHub leak scan" : "Exposed key scan";
  const lines = [`# ${heading}`, "", `- **Target:** \`${r.target}\``, `- **Secrets found:** ${r.total}`];
  if (r.check === "exposed") lines.push(`- **Still live:** ${r.live ?? 0}`);
  lines.push(`- **Credits:** ${r.credits}`, "");
  if (r.findings.length) {
    lines.push("## Findings");
    for (const f of r.findings) {
      const v = f.validation ? ` (${f.validation})` : "";
      lines.push(`- **${f.rule_id}** [${f.severity}] - \`${f.file}:${f.start_line}\` \`${f.preview}\`${v}`);
    }
  } else {
    lines.push("> No exposed secrets found.");
  }
  return lines.join("\n");
}

// Synthesise a clean scan for any custom repo/paste the visitor types in.
function synthClean(check: "github" | "exposed", target: string): ScanResult {
  return {
    check,
    scan_id: "scn_live",
    target,
    total: 0,
    by_severity: {},
    findings: [],
    ...(check === "exposed" ? { live: 0 } : {}),
    credits: 0,
  };
}

// Breaches demo: a tab per check (password, GitHub leaks, exposed keys). Password
// runs live via the k-anonymity endpoint; the two scan checks replay canned
// findings through the shared Visual / JSON / Markdown shell, mirroring the
// malware and email lookup demos.
export function BreachesLiveDemo() {
  const [tab, setTab] = useState<Check>("password");
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limited, setLimited] = useState(false);
  const [result, setResult] = useState<DemoResult | null>(null);
  const [view, setView] = useState<OutputView>("json");

  const active = CHECKS.find((c) => c.id === tab) ?? CHECKS[0];
  const isExample = !result;
  const display = result ?? active.example;
  const verdict = verdictOf(display);
  const resultKey = isExample
    ? "example"
    : isPassword(display)
      ? display.found
        ? "found"
        : "clean"
      : display.scan_id;

  const selectTab = (id: Check) => {
    if (id === tab) return;
    setTab(id);
    setValue("");
    setResult(null);
    setError(null);
    setLimited(false);
    setView("json");
  };

  // Canned scan checks replay a stored result after a short beat.
  const replay = (scan: ScanResult) => {
    if (loading) return;
    setLoading(true);
    setError(null);
    setLimited(false);
    setResult(null);
    setView("json");
    setTimeout(() => {
      setResult(scan);
      setLoading(false);
    }, 650);
  };

  const runPassword = async (pw: string) => {
    const password = pw.trim();
    if (!password || loading) return;
    setLoading(true);
    setError(null);
    setLimited(false);
    setResult(null);
    setView("json");
    try {
      setResult({ check: "password", ...(await demoPasswordBreaches(password)) });
    } catch (e) {
      const err = e as Error & { status?: number };
      if (err.status === 429) setLimited(true);
      else setError(err.message || "Check failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submit = () => {
    const v = value.trim();
    if (!v) return;
    if (tab === "password") void runPassword(v);
    else replay(synthClean(tab, v));
  };

  return (
    <div className="heroui w-full">
      {/* ── Check choices ────────────────────────────── */}
      <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {CHECKS.map((c, i) => {
          const selected = c.id === tab;
          const desktopPosition = ["lg:col-start-2", "lg:col-start-3", "lg:col-start-4"][i];
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => selectTab(c.id)}
              aria-pressed={selected}
              className={`flex h-fit flex-col items-start gap-2 rounded-xl border p-4 text-left transition-colors ${desktopPosition} ${
                selected
                  ? "border-brand bg-sidebar text-text-primary dark:bg-card"
                  : "border-border-tertiary bg-sidebar text-text-primary hover:border-brand/40 dark:bg-card"
              }`}
            >
              <c.icon className="size-5 shrink-0 text-brand" />
              <span className="text-sm font-semibold text-text-primary">{c.label}</span>
              <span className="line-clamp-2 h-10 text-xs leading-relaxed text-text-tertiary">
                {c.description}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mx-auto w-full max-w-xl">
      {/* ── Input ────────────────────────────────────── */}
      <form
        className="flex w-full flex-col gap-2 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={active.placeholder}
          className="h-9 flex-1 bg-sidebar! text-sm dark:bg-card!"
          autoComplete="off"
        />
        <Button type="submit" variant="primary" isDisabled={loading} className="h-9 min-h-9 shrink-0 px-6 text-sm">
          {loading ? <Loader2 className="size-4 animate-spin" /> : active.cta}
        </Button>
      </form>

      {/* ── Results card (always visible; shows an example until a real run) ── */}
      <Card
        variant="default"
        className="mt-5 gap-0! overflow-hidden rounded-2xl border border-border-tertiary bg-sidebar! p-0! shadow-none dark:bg-card!"
      >
        {error ? (
          <div className="flex items-center gap-2 p-4 text-sm text-danger">
            <AlertCircle className="size-4 shrink-0" />
            {error}
          </div>
        ) : limited ? (
          <p className="p-4 text-sm text-text-tertiary">You&apos;ve hit the free demo limit. Sign up to keep checking.</p>
        ) : (
          <>
            {/* Header: endpoint + output-view tabs */}
            <div className="flex items-center gap-2 border-b border-border-tertiary px-3 py-2">
              {isExample && (
                <span className="shrink-0 rounded-full bg-brand/10 px-1.5 py-0.5 text-4xs tracking-wider text-brand uppercase">
                  Example
                </span>
              )}
              {active.tag && (
                <span className="shrink-0 rounded-full bg-warning/10 px-1.5 py-0.5 text-4xs tracking-wider text-warning uppercase">
                  {active.tag}
                </span>
              )}
              <span className="truncate font-mono text-2xs text-text-quaternary">{active.endpoint}</span>
              <div className="ml-auto flex items-center gap-1">
                {OUTPUT_TABS.map((o) => {
                  const selected = o.id === view;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setView(o.id)}
                      aria-pressed={selected}
                      className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-2xs font-medium transition-colors ${
                        selected
                          ? "bg-brand/10 text-brand"
                          : "text-text-tertiary hover:text-text-primary"
                      }`}
                    >
                      <o.icon className="size-3.5" />
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Body */}
            <AnimatePresence mode="wait">
              <motion.div
                key={`${tab}-${resultKey}-${view}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="min-h-60"
              >
                {view === "visual" ? (
                  <div className="flex min-h-60 flex-col items-center justify-center gap-4 p-6 text-center">
                    <span
                      className={`flex size-12 items-center justify-center rounded-xl ${
                        verdict.danger ? "bg-danger/10 text-danger" : "bg-brand/10 text-brand"
                      }`}
                    >
                      <verdict.Icon className="size-5" />
                    </span>
                    <div>
                      <p className="text-base font-semibold text-text-primary">{verdict.title}</p>
                      <p className="mt-1 max-w-sm text-sm text-text-tertiary">{verdict.subtitle}</p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
                      {metasFor(display).map((m) => (
                        <Meta key={m.k} k={m.k} v={m.v} mono={m.mono} />
                      ))}
                    </div>
                    {!isPassword(display) && display.findings.length > 0 && (
                      <ul className="w-full max-w-sm space-y-1.5 text-left">
                        {display.findings.map((f) => (
                          <li
                            key={`${f.rule_id}-${f.start_line}`}
                            className="flex items-center gap-2 rounded-lg border border-border-tertiary px-2.5 py-1.5"
                          >
                            <span
                              className={`shrink-0 rounded-full px-1.5 py-0.5 text-4xs uppercase tracking-wider ${SEVERITY_TINT[f.severity]}`}
                            >
                              {f.severity}
                            </span>
                            <span className="truncate font-mono text-2xs text-text-secondary">{f.rule_id}</span>
                            <span className="ml-auto shrink-0 font-mono text-4xs text-text-quaternary">
                              {f.file}:{f.start_line}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <div className="text-left">
                    <CodeBlock
                      code={view === "json" ? JSON.stringify(display, null, 2) : resultMarkdown(display)}
                      language={view === "json" ? "json" : "markdown"}
                    />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </>
        )}
      </Card>

      {/* ── Try one (below results, centred) ──────── */}
      <div className="mt-4 flex flex-col items-center gap-2">
        <span className="text-2xs text-text-quaternary">Try one</span>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {active.samples.map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => {
                if (s.scan) replay(s.scan);
                else if (s.value) {
                  setValue(s.value);
                  runPassword(s.value);
                }
              }}
              className="max-w-full truncate rounded-full border border-border-tertiary bg-sidebar px-2.5 py-1 font-mono text-2xs text-text-secondary transition-colors hover:border-brand/40 hover:text-text-primary dark:bg-card"
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}

function Meta({ k, v, mono = false }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-4xs uppercase tracking-wider text-text-quaternary">{k}</span>
      <span className={`text-xs font-medium text-text-secondary ${mono ? "font-mono" : ""}`}>{v}</span>
    </div>
  );
}
